import { FC } from "react"
import { useFormik } from "formik"
import Modal, { ModalProps } from "@/components/Modal"
import OutlineButton from "@/components/OutlineButton"
import Button from "@/components/Button"
import TextField from "@/components/TextField"
import Textarea from "@/components/Textarea"
import Select from "@/components/Select"
import { getErrorMessage } from "@/core/api/helpers"
import { useAppDispatch } from "@/redux/hooks"
import { useGetSystemValuesQuery } from "@/redux/services/generalApi"
import { useAddSearchCriteriaCookieMutation } from "@/redux/services/searchCriteriaApi"
import { setToast } from "@/redux/toastSlice"

type Props = {
  onSuccess?: () => void
} & ModalProps

const SearchCriteriaCookieModal: FC<Props> = ({
  open,
  onClose,
  onSuccess,
}: Props) => {
  const dispatch = useAppDispatch()
  const [addCookie, { isLoading }] = useAddSearchCriteriaCookieMutation()
  const { data: { regions } = { regions: [] }, isLoading: valuesLoading } =
    useGetSystemValuesQuery(undefined, { skip: !open })
  const {
    getFieldProps,
    handleSubmit,
    resetForm,
    setFieldValue,
    values: { name, cookieString, region },
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      cookieString: "",
      region: "",
    },
    onSubmit: async (body) => {
      try {
        await addCookie(body).unwrap()
        dispatch(
          setToast({
            show: true,
            variant: "success",
            message: "Success add LinkedIn cookie",
          }),
        )
        resetForm()
        onSuccess && onSuccess()
        onClose && onClose()
      } catch (error) {
        dispatch(
          setToast({
            show: true,
            variant: "error",
            message: getErrorMessage(error),
          }),
        )
      }
    },
  })
  const handleClose = () => {
    resetForm()
    onClose && onClose()
  }
  const submitDisabled = isLoading || !name || !cookieString || !region

  return (
    <Modal
      title="Add LinkedIn Cookie"
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      actions={
        <div className="flex gap-4">
          <OutlineButton
            type="button"
            className="btn-sm text-black min-w-[86px]"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </OutlineButton>
          <Button
            disabled={submitDisabled}
            type="submit"
            className="btn-sm min-w-[86px]"
          >
            Add
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <TextField
          topLeftLabel="Cookie Name"
          placeholder="Enter cookie name"
          className="input-sm"
          {...getFieldProps("name")}
        />
        <Textarea
          topLeftLabel="Cookie String"
          placeholder="Paste your LinkedIn cookie cURL string here"
          className="input-sm"
          {...getFieldProps("cookieString")}
        />
        <Select
          value={region || null}
          onChange={(v) => setFieldValue("region", v)}
          label="Region"
          placeholder="Select region"
          className="h-10 bg-input rounded-md"
          bordered={false}
          options={regions.map(({ id, name }) => ({
            label: name,
            value: id,
          }))}
          getPopupContainer={(node) => node.parentNode as HTMLElement}
          loading={valuesLoading}
        />
      </div>
    </Modal>
  )
}

export default SearchCriteriaCookieModal
